// ═══════════════════════════════════════════════════════════════════════════════
// sampleOutputs.ts — canonical "this is what the LLM sees" examples per element
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   The Element Catalog page shows clinical SMEs what each briefing element
//   actually injects into the agent's system prompt at runtime. Instead of
//   abstract names like "IdentityDemographicsProvider", an SME sees a concrete
//   one-line example pulled straight from the provider's render logic.
//
//   This is documentation, not runtime data. It lives in the frontend so SMEs
//   get an immediate preview without backend DI/PHI concerns, and so updates
//   ship with the UI rather than requiring a DB migration.
//
// AUTHORING RULES
//   - Each sample should be a REPRESENTATIVE one-line rendering — what the
//     provider would emit for a realistic patient. Lift from the provider's
//     own render code (DATA SOURCE / STATUS RENDERING blocks in the .cs file).
//   - Prefer common cases ("Active treatment protocol: FOLFOX (started 2026-04-15)")
//     over edge cases ("Empty: no protocol found").
//   - Use placeholder PHI (initials, generic ages, common conditions). NEVER
//     real patient names or identifiers.
//   - Keep it short: 1 line is best, 2 if absolutely needed. The LLM context
//     is precious; long samples teach the wrong lesson.
//
// HOW IT'S CONSUMED
//   ElementCatalogPage uses `getSampleOutput(code)` to pull the sample for a
//   row. Missing codes (e.g. SME-added custom elements not yet in this map)
//   fall back to a friendly "no sample yet" placeholder, with a hint to
//   authors that they can update this file.
// ═══════════════════════════════════════════════════════════════════════════════

interface ElementSample {
  /** One-line preview the LLM would see. */
  example: string;
  /** Plain-language explanation of what this element gives the agent. */
  intent?: string;
}

/**
 * Canonical sample output for each known briefing element Code. Authored
 * from the providers in OO.Business/AIInfo/BriefingContextInfo/Providers/.
 * Update this file when you change the provider's render shape.
 */
const SAMPLES: Record<string, ElementSample> = {
  // ── Identity ────────────────────────────────────────────────────────────────
  'identity.demographics': {
    example: 'Patient: J.M., 62-year-old female.',
    intent:
      'A one-line identity header so the agent has the patient’s initials, age, and sex at hand for every turn.',
  },
  'identity.language': {
    example: 'Preferred language: Spanish.',
    intent:
      'Tells the agent what language to address the patient in. The agent should switch tone + word choice accordingly.',
  },
  'identity.personal-family-context': {
    example:
      'Personal & family context: retired teacher; two adult children + 3 grandkids; flexible mornings; daughter drives her to appointments. Upcoming: Granddaughter’s wedding (2026-07-15) — wants to feel well enough to attend.',
    intent:
      'Gives the agent the patient’s life situation and upcoming milestones so it can time care around what matters to them and connect with empathy.',
  },

  // ── Clinical Baseline ───────────────────────────────────────────────────────
  'clinical.primary-diagnosis': {
    example: 'Primary diagnosis: Stage IIIB colorectal cancer (C18.9).',
    intent:
      'Grounds the conversation in what the patient is being treated for. The agent never has to ask.',
  },
  'clinical.active-protocol': {
    example: 'Active treatment protocol: FOLFOX (started 2026-04-15).',
    intent:
      'Names the current treatment regimen + when it started. Drives cycle-aware guidance throughout the conversation.',
  },
  'clinical.biomarkers': {
    example: 'Biomarkers: KRAS wild-type, MSI-stable, BRAF wild-type.',
    intent:
      'Key molecular markers the agent should consider when discussing treatment options or trials.',
  },
  'clinical.allergies': {
    example: 'Allergies: penicillin (anaphylaxis), sulfa (hives).',
    intent:
      'Safety-critical — the agent must never suggest a contraindicated medication.',
  },
  'clinical.vascular-access': {
    example:
      'IV access: DIFFICULT — ultrasound · warm pack · 2-attempt limit · avoid Left arm. Avoid Left arm: lymphedema. Best first stick: Right forearm, 24G (3 first-stick successes).',
    intent:
      'The standing vein plan before any IV conversation — site bans carry clinical reasons (lymphedema, fistula), and central access inverts the guidance entirely. Silent for unremarkable access.',
  },
  'clinical.care-team': {
    example: 'Care team: Dr. R. Chen (medical oncology); RN K. Patel.',
    intent:
      'So the agent can reference the right people by role when escalating or directing the patient.',
  },
  'clinical.caregiver': {
    example: 'Primary caregiver: spouse, J. Lee.',
    intent:
      'Recognizes the family member supporting the patient — the agent should acknowledge them by role.',
  },
  'clinical.advance-directives': {
    example: 'Advance directives: DNR on file; healthcare proxy designated.',
    intent:
      'Critical clinical context for end-of-life or escalation conversations.',
  },
  'clinical.screenings': {
    example: 'Screenings: colonoscopy due (last 2021); mammogram current (2026-01).',
    intent:
      'Surfaces preventive-care gaps so the agent can prompt about overdue screenings.',
  },

  // ── Treatment State ─────────────────────────────────────────────────────────
  'state.cycle-day': {
    example: 'Treatment cycle: FOLFOX cycle 4, day 8 (C4D8).',
    intent:
      'Day-of-cycle precision — lets the agent calibrate expectations for fatigue, nadir, and timing.',
  },
  'state.recent-labs': {
    example: 'Recent labs (2026-05-19, Labcorp): Creatinine 1.4 mg/dL [High], eGFR 48 mL/min [Low].',
    intent:
      'Last-drawn provenance plus the recent abnormal/critical markers (from the canonical labs schema) so the agent flags out-of-range values without asking.',
  },
  'state.active-medications': {
    example:
      'Current medications: ondansetron 8 mg PO q8h PRN; dexamethasone 4 mg taper.',
    intent:
      'What the patient is actually taking right now. Drives safe coaching on timing, side effects, and interactions.',
  },
  'state.active-ctcae-aes': {
    example:
      'Active symptoms (CTCAE): Grade 2 peripheral neuropathy; Grade 1 fatigue.',
    intent:
      'Currently-graded toxicities. The agent can ask follow-ups calibrated to the current grade instead of starting from zero.',
  },
  'state.cumulative-dose': {
    example: 'Cumulative oxaliplatin: 720 mg/m² (75% of typical neurotoxic threshold).',
    intent:
      'Running total of dose-limiting agents so the agent can flag approaching thresholds.',
  },
  'state.intervention-history': {
    example:
      'Recent interventions: dose held at C3 (Gr 3 neutropenia); G-CSF given C3D2.',
    intent:
      'What’s already been done in response to prior issues — keeps the agent from suggesting things that were already tried.',
  },
  'state.escalation-flags': {
    example: 'Escalation flag: fever 101.4 °F reported 2 hours ago — triage active.',
    intent:
      'Urgent flags. If this is set, the agent should slow down and orient the conversation around the active concern.',
  },
  'state.recent-vitals': {
    example: 'Vitals (2026-05-20): BP 124/78, HR 78, Temp 98.4 °F, SpO₂ 98%.',
    intent:
      'Most-recent vital signs the patient or care-team recorded. Anchors the agent in what “now” looks like.',
  },
  'state.last-encounter-summary': {
    example:
      'Last visit (2026-05-12): tolerating FOLFOX; mild neuropathy noted; next infusion 5/26.',
    intent:
      'One-line readout of the most recent in-person visit. Gives the agent fresh continuity without re-asking.',
  },

  // ── Symptoms / care coordination (under state.* namespace) ──────────────────
  'state.recent-symptoms-24h': {
    example: 'Symptoms (last 24h): nausea 4/10 after lunch; loose stool x2.',
    intent:
      'Patient-reported symptoms from the last day. Drives the agent’s opening "how are you feeling?" with specifics already in hand.',
  },
  'state.symptom-context': {
    example:
      'Symptoms (what the patient sees): FOLFOX, cycle 3 of 12, day 11. Open reports: loose or watery stools (17 Oct, told someone from the care team will call today). Their protocol’s symptom list: feeling sick to your stomach; loose or watery stools; tingling or numbness in hands or feet.',
    intent:
      'Patient agents only. The course in the chat, what the patient reported and was told, and their list in their words — so the agent files new symptoms from the list and answers "did anyone see this?". Never a grade.',
  },
  'state.diabetes-snapshot': {
    example:
      'Diabetes snapshot: A1c 7.4% (2026-04); typical fasting 130–150 mg/dL; on metformin + basal insulin.',
    intent:
      'Compact diabetes context for any chronic-care agent that touches glucose management.',
  },
  'state.cgm-recent': {
    example:
      'CGM last 14d: TIR 62%; nocturnal lows 3; coefficient of variation 31%.',
    intent:
      'CGM-derived metrics so the agent can discuss time-in-range without asking the patient to summarize their device.',
  },
  'state.ckd-status': {
    example:
      'Kidney status: CKD G4, KDIGO Very High risk — eGFR 17, albuminuria A3 (2026-05-20) — on hemodialysis (3x weekly).',
    intent:
      'Kidney acuity at a glance — CKD stage and KDIGO risk are the signal the care team triages outreach urgency on; dialysis/transplant status frames the conversation.',
  },
  'state.mental-health-screening': {
    example:
      'Mental-health screening trend: PHQ-9 dropped from 12 → 7 over 4 weeks; GAD-7 stable at 5.',
    intent:
      'Trend of depression/anxiety screening scores. Lets the agent acknowledge progress (or concern) by name.',
  },
  'state.appointments-upcoming': {
    example:
      'Upcoming appointments: 5/26 infusion; 6/03 medical oncology follow-up.',
    intent:
      'Next-7-days calendar context. The agent can refer to upcoming visits without asking.',
  },
  'state.patient-goals': {
    example:
      'Patient goals: stay strong enough for granddaughter’s wedding 6/15; minimize nausea.',
    intent:
      'What the patient cares about. Anchors recommendations to outcomes that matter to them, not just clinical metrics.',
  },
  'state.education-history': {
    example:
      'Education assigned: “Managing FOLFOX side effects” (viewed 2026-05-10); “When to call the nurse line” (assigned, not viewed).',
    intent:
      'What educational content the patient has been given. Prevents the agent from re-pitching material they’ve already seen.',
  },
};

/**
 * Look up the canonical sample for an element. Resolution order:
 *
 *   1. The element row's `sampleOutput` column, if populated (SME-authored
 *      from the catalog editor). Post-Phase-4 this is the source of truth.
 *   2. This file's hardcoded SAMPLES map, keyed by Code. Fallback for legacy
 *      rows still NULL in the DB or for custom SME-created elements that
 *      pre-date the editor field.
 *   3. A friendly "(no sample yet)" placeholder for everything else.
 *
 * The `intent` field is never stored in the DB — it's always sourced from
 * this file. SME-authored DB samples only override the `example` text.
 */
export function getSampleOutput(
  elementOrCode:
    | string
    | undefined
    | null
    | { code?: string | null; sampleOutput?: string | null },
): ElementSample {
  // Resolve code + dbSample from either signature.
  const code =
    typeof elementOrCode === 'string'
      ? elementOrCode
      : elementOrCode?.code ?? null;
  const dbSample =
    typeof elementOrCode === 'object' && elementOrCode
      ? elementOrCode.sampleOutput ?? null
      : null;

  // Hardcoded sample (intent always comes from here when present).
  const baseline = code ? SAMPLES[code] : undefined;

  if (dbSample && dbSample.trim()) {
    return {
      example: dbSample,
      intent: baseline?.intent,
    };
  }
  if (baseline) return baseline;

  if (!code) {
    return {
      example: '(no sample yet)',
      intent: 'This element doesn’t have a documented sample output.',
    };
  }
  return {
    example: '(no sample yet)',
    intent:
      'No documented sample for this element yet. Edit the element to author one — SMEs will see it in the Preview drawer.',
  };
}
